"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import { GripVertical } from "lucide-react";

export interface SortListItem {
  id: string;
  content: React.ReactNode;
}

export interface SortListProps {
  items: SortListItem[];
  onReorder: (items: SortListItem[]) => void;
  className?: string;
}

export function SortList({ items, onReorder, className }: SortListProps) {
  const [draggedIndex, setDraggedIndex] = React.useState<number | null>(null);
  const [overIndex, setOverIndex] = React.useState<number | null>(null);

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setOverIndex(index);
  };

  const handleDrop = (index: number) => {
    if (draggedIndex === null || draggedIndex === index) {
      setDraggedIndex(null);
      setOverIndex(null);
      return;
    }

    const newItems = [...items];
    const [removed] = newItems.splice(draggedIndex, 1);
    newItems.splice(index, 0, removed);
    onReorder(newItems);
    setDraggedIndex(null);
    setOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setOverIndex(null);
  };

  return (
    <div className={cn("space-y-1", className)}>
      {items.map((item, index) => (
        <div
          key={item.id}
          draggable
          onDragStart={() => handleDragStart(index)}
          onDragOver={(e) => handleDragOver(e, index)}
          onDrop={() => handleDrop(index)}
          onDragEnd={handleDragEnd}
          className={cn(
            "flex items-center gap-2 rounded-2xl border border-slate-200/70 bg-white/90 px-3 py-2.5 transition-all dark:border-zinc-800 dark:bg-zinc-900/85",
            draggedIndex === index && "opacity-50",
            overIndex === index &&
              draggedIndex !== index &&
              "border-indigo-400 bg-indigo-50/50 dark:border-indigo-500 dark:bg-indigo-900/20"
          )}
        >
          <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-slate-400 active:cursor-grabbing dark:text-zinc-500" />
          <div className="flex-1">{item.content}</div>
        </div>
      ))}
    </div>
  );
}
